import { type QueryFunctionContext } from "@tanstack/react-query";
import { type BarChartData } from "components/charts/types/Shared";
import {
  completes,
  lineChartFactory,
  starts,
  type PerformanceFilter,
} from "features/Library/Common/utils/performanceUtils";
import { reportingColors } from "features/Library/Common/utils/constants";
import groupsDataService from "features/People/services/groupsDataService";
import { type ChartPeriod } from "hooks/useChartPeriodMeasure";
import moment from "moment";
import { SERVER_DATE_ONLY_FORMAT } from "utils/dateTimeUtils";
import {
  type GroupActivityMetrics,
  type PrioritizedFlowLineChart,
  type PrioritizedFlowTable,
} from "../Performance/types";
import { type DataPoint } from "components/charts/types/HorizontalBarChart";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSquareArrowDown, faSquareArrowUp, faSquareExclamation } from "@fortawesome/pro-solid-svg-icons";
import styles from "../../../../../components/cards/CardReporting/CardReporting.module.scss";
import { type CardReportingItem } from "components/cards/CardReporting/CardReporting";
import { type PaginationParams } from "features/Library/Common/models";
import { type FetchPageResult } from "features/Reporting/Content/shared/GenericPerformanceListWithServerPagination";

const countHeaderName = process.env.REACT_APP_COUNT_HEADER_NAME as string;

type QueryFilterGroupId = QueryFunctionContext<[_: string, filter: PerformanceFilter, groupId: number], unknown>;

type QueryFilterGroupIdPeriod = QueryFunctionContext<
  [_: string, filter: PerformanceFilter, groupId: number, period: ChartPeriod],
  unknown
>;

type PrioritizedBarMetrics = {
  Incomplete: number;
  NotStarted: number;
  Started: number;
  Completed: number;
};

type PrioritizedFlowCardsResult = {
  cards: CardReportingItem[];
  barData: DataPoint[];
};

type PrioritizedFlowActivityMetrics = Pick<
  PrioritizedFlowLineChart,
  "Incomplete" | "NotStarted" | "Started" | "Completed"
>;

export const getSummaryCards = async ({ queryKey }: QueryFilterGroupId): Promise<CardReportingItem[]> => {
  const { data } = await groupsDataService.getGroupPerformanceSummaryCards(queryKey[1], queryKey[2]);

  return [
    {
      statDescription: "Enrolled",
      stat: data.TotalUsers,
      popupBody: "Total users enrolled for this group during the specified time period.",
    },
    {
      statDescription: "Prioritized Flows",
      stat: data.TotalPrioritizedFlows,
      popupBody: "Total prioritized Flows in this group during the specified time period.",
    },
    {
      statDescription: "Packs",
      stat: data.TotalGroupPacks,
      popupBody: "Total Packs included with this group during the specified time period.",
    },
  ];
};

export const getLineChartQuery = async ({ queryKey }: QueryFilterGroupIdPeriod) => {
  const { data } = await groupsDataService.getGroupPerformanceUserActivityLineChart(
    queryKey[1],
    queryKey[2],
    queryKey[3],
  );

  const lineData = lineChartFactory(data);

  const activityBarData: BarChartData<GroupActivityMetrics>[] = data.map((item) => ({
    group: moment(item.Date).format(SERVER_DATE_ONLY_FORMAT),
    TotalOutreach: item.TotalOutreach,
    TotalInteractions: item.TotalInteractions,
  }));

  const order: (keyof GroupActivityMetrics)[] = ["TotalOutreach", "TotalInteractions"];

  return { lineData, activityBarData, order };
};

export const getPrioritizedFlowLineChart = async ({ queryKey }: QueryFilterGroupIdPeriod) => {
  const [, filter, groupId, period] = queryKey;
  const { data } = await groupsDataService.getGroupPerformancePrioritizedFlowLineChart(filter, groupId, period);

  const lineData = lineChartFactory(data);

  const activityBarData: BarChartData<PrioritizedFlowActivityMetrics>[] = data.map((item) => ({
    group: moment(item.Date).format(SERVER_DATE_ONLY_FORMAT),
    Incomplete: item.Incomplete,
    NotStarted: item.NotStarted,
    Started: item.Started,
    Completed: item.Completed,
  }));

  const order: (keyof PrioritizedFlowActivityMetrics)[] = ["Incomplete", "NotStarted", "Started", "Completed"];

  return { lineData, activityBarData, order };
};

export const getUserActivityEngagement = async ({ queryKey }: QueryFilterGroupId): Promise<CardReportingItem[]> => {
  const { data } = await groupsDataService.getGroupPerformanceUserActivityEngagement(queryKey[1], queryKey[2]);

  return [
    {
      statDescription: "Platform Outreach",
      stat: data.TotalOutreach,
      popupBody:
        "Total number of outreach attempts, emails, messages, etc. to engage users during the specified time period.",
    },
    {
      statDescription: "Platform Interactions",
      stat: data.TotalInteractions,
      popupBody:
        "Total number of user interactions with the platform, emails, messages, etc. during the specified time period.",
    },
  ];
};

export const getUserEnrollmentLineChart = async ({ queryKey }: QueryFilterGroupIdPeriod) => {
  const { data } = await groupsDataService.getGroupPerformanceUserEnrollmentLineChart(
    queryKey[1],
    queryKey[2],
    queryKey[3],
  );

  return lineChartFactory(
    data.map((item) => ({
      Date: item.Date,
      totalJoined: item.TotalJoined,
      totalLeft: item.TotalLeft,
      totalMembers: item.TotalMembers,
    })),
  );
};

export const getUserEnrollmentCards = async ({ queryKey }: QueryFilterGroupId): Promise<CardReportingItem[]> => {
  const { data } = await groupsDataService.getGroupPerformanceUserEnrollmentCards(queryKey[1], queryKey[2]);

  return [
    {
      statDescription: "Currently Enrolled",
      stat: data.TotalMembers,
      popupBody: "Total users currently enrolled for this group at the end of the specified time period.",
    },
    {
      statDescription: "Dropped",
      stat: data.TotalDropped,
      popupBody: "Total users dropped from this group during the specified time period.",
    },
    {
      statDescription: "Drop Rate",
      stat: data.DropRate * 100,
      isStatPercentage: true,
      popupBody:
        "Percentage of users who dropped from this group based on enrollment criteria during the specified time period.",
    },
  ];
};

export const getFlowEngagementChart = async ({ queryKey }: QueryFilterGroupId) => {
  const { data } = await groupsDataService.getGroupPerformanceFlowEngagementChart(queryKey[1], queryKey[2]);

  const barData: DataPoint[] = [
    {
      id: starts.toLowerCase(),
      category: starts,
      value: data.TotalStartedFlows,
      fillColor: reportingColors.blue,
    },
    {
      id: completes.toLowerCase(),
      category: completes,
      value: data.TotalCompletedFlows,
      fillColor: reportingColors.yellow,
    },
  ];

  const cards: CardReportingItem[] = [
    {
      statDescription: "Active Users",
      stat: data.TotalUsers,
      popupBody: "Total active users in all Flows for this group during the selected period.",
    },
  ];

  return { barData, cards };
};

export const getEmailEngagementChart = async ({ queryKey }: QueryFilterGroupId) => {
  const { data } = await groupsDataService.getGroupPerformanceEmailEngagementChart(queryKey[1], queryKey[2]);

  return [
    {
      id: "sends",
      category: "Sends",
      value: data.TotalEmailSends,
      fillColor: reportingColors.blue,
    },
    {
      id: "opens",
      category: "Opens",
      value: data.TotalEmailOpens,
      fillColor: reportingColors.greenDark,
    },
    {
      id: "clicks",
      category: "Clicks",
      value: data.TotalEmailClicks,
      fillColor: reportingColors.yellow,
    },
  ];
};

export const getEmailEngagementCards = async ({ queryKey }: QueryFilterGroupId): Promise<CardReportingItem[]> => {
  const { data } = await groupsDataService.getGroupPerformanceEmailEngagementCards(queryKey[1], queryKey[2]);

  return [
    {
      statDescription: "Email Sends",
      stat: data.TotalEmailSends,
      popupBody: "The total number of email sends during the specified time period.",
    },
    {
      statDescription: "Email Opens",
      stat: data.TotalEmailOpens,
      popupBody: "The total number of emails opened during the specified time period.",
    },
    {
      statDescription: "Email Open Rate",
      stat: data.EmailOpenRate * 100,
      popupBody: "The percentage of emails opened compared to the total number of emails sent.",
      isStatPercentage: true,
    },
    {
      statDescription: "Email CTR",
      stat: data.ClickThroughRate * 100,
      popupBody: "The percentage of emails clicked compared to the total number of emails sent.",
      isStatPercentage: true,
    },
  ];
};

export const getPrioritizedFlowCards = async ({
  queryKey,
}: QueryFilterGroupId): Promise<PrioritizedFlowCardsResult> => {
  const [, filter, groupId] = queryKey;
  const { data } = await groupsDataService.getGroupPerformancePrioritizedFlowCards(filter, groupId);

  const cards: CardReportingItem[] = [
    {
      statDescription: "Prioritized Flows",
      stat: data.Prioritized,
      popupBody: "The total number of Flows prioritized.",
    },
    {
      statDescription: (
        <div className={styles.priorityDescription}>
          <FontAwesomeIcon icon={faSquareExclamation} />
          <div>Required</div>
        </div>
      ),
      key: "requiredFlows",
      stat: data.Required,
      popupBody: "The total number of Flows with required priority.",
    },
    {
      statDescription: (
        <div className={styles.priorityDescription}>
          <FontAwesomeIcon icon={faSquareArrowUp} />
          <div>Important</div>
        </div>
      ),
      key: "importantFlows",
      stat: data.Important,
      popupBody: "The total number of Flows with important priority.",
    },
    {
      statDescription: (
        <div className={styles.priorityDescription}>
          <FontAwesomeIcon icon={faSquareArrowDown} />
          <div>Recommended</div>
        </div>
      ),
      key: "recommendedFlows",
      stat: data.Recommended,
      popupBody: "The total number of Flows with recommended priority.",
    },
  ];

  const statusMetrics: PrioritizedBarMetrics = {
    Incomplete: data.Incomplete,
    NotStarted: data.NotStarted,
    Started: data.Started,
    Completed: data.Completed,
  };

  const barData: DataPoint[] = [
    {
      id: "incomplete",
      category: "Incomplete",
      value: statusMetrics.Incomplete,
      fillColor: reportingColors.greyLight,
    },
    {
      id: "notStarted",
      category: "Not Started",
      value: statusMetrics.NotStarted,
      fillColor: reportingColors.greyLighter,
    },
    {
      id: "started",
      category: "Started",
      value: statusMetrics.Started,
      fillColor: reportingColors.yellow,
    },
    {
      id: "completed",
      category: "Completed",
      value: statusMetrics.Completed,
      fillColor: reportingColors.greenLight,
    },
  ];

  return {
    cards,
    barData,
  };
};

export const fetchPrioritizedFlowTable = async (
  filter: PerformanceFilter,
  groupId: number,
  pagination: PaginationParams,
  signal?: AbortSignal,
): Promise<FetchPageResult<PrioritizedFlowTable>> => {
  const response = await groupsDataService.getGroupPerformancePrioritizedFlowTable(filter, groupId, pagination, signal);

  return {
    rows: response.data,
    totalCount: Number.parseInt(response.headers[countHeaderName]),
  };
};
